import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "@bitsentry-ce/i18n";

// One id per message, so every mounted hook updates the same toast instead of stacking copies.
const CONNECTION_LOST_TOAST_ID = "connection-lost";
const CONNECTION_RESTORED_TOAST_ID = "connection-restored";

export function useOnlineStatus() {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.dismiss(CONNECTION_LOST_TOAST_ID);
      if (wasOffline) {
        toast.success(t("common.useOnlineStatus.connectionRestored"), {
          id: CONNECTION_RESTORED_TOAST_ID,
          description: t("common.useOnlineStatus.youAreBackOnline"),
        });
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      toast.error(t("common.useOnlineStatus.connectionLost"), {
        id: CONNECTION_LOST_TOAST_ID,
        description: t("common.useOnlineStatus.workingOfflineDescription"),
        duration: Infinity,
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [wasOffline, t]);

  return isOnline;
}
